import { useState, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import styled from '@emotion/styled';
import type { API, QuickChartData } from '@trinity/chat';
import { LegendCollapseToggleContext } from '@trinity/chat-components/components/Chat/context.js';
import { NivoBarChart } from '@trinity/chat-components/components/Chat.Charts/Bar.js';
import { ChartLegend } from '@trinity/chat-components/components/Chat.Charts/Legend.js';
import { NivoMultipleBarChart } from '@trinity/chat-components/components/Chat.Charts/MultipleBar.js';
import { NivoPieChart } from '@trinity/chat-components/components/Chat.Charts/Pie.js';
import { NivoStackedBarChart } from '@trinity/chat-components/components/Chat.Charts/StackedBar.js';
import { AnalysisTable } from '@trinity/chat-components/components/QuantAnalysis/Analysis.Table.js';
import { KPIWidget } from '@trinity/chat-components/components/QuantAnalysis/KPIWidget.js';
import { useOptionsColors } from '../../SurveyResponses/hooks/usePaletteColors';

type QuantFileTableData = API.ProjectsQuantAnalysis.QuantFileTableData;

type Props = {
  chart: QuickChartData;
  legendOpenDefault?: boolean;
};

export type ChartVisualizationRef = {
  getImageExportRef: () => HTMLTableElement | null;
};

export const ChartVisualization = forwardRef<ChartVisualizationRef, Props>(({ chart, legendOpenDefault = false }, ref) => {
  const [legendOpen, setLegendOpen] = useState(legendOpenDefault);
  const imageExportRef = useRef<HTMLTableElement>(null);

  const chartColors = useOptionsColors();

  useImperativeHandle(ref, () => ({
    getImageExportRef: () => imageExportRef.current,
  }), []);

  const legend = useMemo(() => {
    const legendData: { key: string; color: string }[] = [];

    switch (chart.type) {
      case 'bar':
      case 'stacked-bar': {
        const data = chart.chart.data;
        for (let i = 0; i < data.length; i++) {
          legendData.push({ key: data[i].x, color: chartColors[i % chartColors.length] });
        }
        break;
      }

      case 'pie': {
        const data = chart.chart.data;
        for (let i = 0; i < data.length; i++) {
          legendData.push({ key: data[i].label, color: chartColors[i % chartColors.length] });
        }
        break;
      }

      case 'multiple-clustered-bar':
      case 'multiple-stacked-bar': {
        const chartData = chart.chart;
        const keysSet = new Set<string>();

        for (let i = 0; i < chartData.data.length; i++) {
          for (let j = 0; j < chartData.data[i].data.length; j++) {
            keysSet.add(chartData.data[i].data[j].x);
          }
        }

        const keys = Array.from(keysSet);
        for (let i = 0; i < keys.length; i++) {
          legendData.push({ key: keys[i], color: chartColors[i % chartColors.length] });
        }
        break;
      }

      case 'data-table':
        // Data tables don't have legends
        break;

      default:
        break;
    }

    return legendData;
  }, [chart, chartColors]);

  return (
    <LegendCollapseToggleContext.Provider value={[legendOpen, setLegendOpen]}>
      <ChartContainer>
        <ChartContent>
          {
            renderChart()}
        </ChartContent>
        {legend.length > 0 && <ChartLegend legend={legend} />}
      </ChartContainer>
    </LegendCollapseToggleContext.Provider>
  );

  function renderChart() {
    switch (chart.type) {
      case 'bar':
      case 'stacked-bar': {
        const barChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
        return chart.type === 'bar'
          ? <NivoBarChart
            chart={chart.chart}
            colors={barChartColors} />
          : <NivoStackedBarChart
            chart={chart.chart}
            colors={barChartColors} />;
      }

      case 'pie': {
        const pieChartColors = chart.chart.data.map((_, i) => chartColors[i % chartColors.length]);
        return (
          <NivoPieChart
            chart={chart.chart}
            colors={pieChartColors} />
        );
      }

      case 'multiple-clustered-bar':
      case 'multiple-stacked-bar': {
        const allKeys = new Set<string>();
        chart.chart.data.forEach(series => {
          series.data.forEach(d => allKeys.add(d.x));
        });
        const keys = Array.from(allKeys);
        const multiBarChartColors = keys.map((_, i) => chartColors[i % chartColors.length]);

        return (
          <NivoMultipleBarChart
            isStacked={chart.type === 'multiple-stacked-bar'}
            chart={chart.chart}
            colors={multiBarChartColors} />
        );
      }

      case 'data-table': {
        // Create table data with custom labels if provided
        const tableData: QuantFileTableData = {
          columnNames: chart.customColumnNames || chart.data.columnNames,
          rowNames: chart.customRowNames || chart.data.rowNames,
          records: chart.data.records,
          metadata: chart.data.metadata,
        };

        return (
          <DataTableContainer>
            <AnalysisTable
              ref={imageExportRef}
              data={tableData}
              headerBackgroundColor={chart.headerColor}
              initialSortConfig={chart.sortConfig}
              disableSorting={true}
              disableSelection={true} />
          </DataTableContainer>
        );
      }

      case 'kpi-widget': {
        return (
          <KPIWidgetContainer>
            <KPIWidget
              ref={imageExportRef}
              title={chart.title}
              backgroundColor={chart.backgroundColor}
              values={chart.values}
              labels={chart.labels}
              insights={chart.insights} />
          </KPIWidgetContainer>
        );
      }

      default:
        throw new Error(`Unsupported chart type`);
    }
  }
});

ChartVisualization.displayName = 'ChartVisualization';

const ChartContainer = styled.div({
  position: 'relative',
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const ChartContent = styled.div({
  flex: 1,
  minHeight: '300px',
  width: '100%',
});

const DataTableContainer = styled.div({
  width: '100%',
  overflow: 'hidden',
});

const KPIWidgetContainer = styled.div({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  overflow: 'hidden',
  minWidth: 0, // Allow flex item to shrink below its minimum content size
  '& > *': {
    maxWidth: '100%',
    minWidth: 0,
    flex: '0 1 auto', // Allow shrinking
  },
});
